import { defineComponent as _defineComponent } from 'vue'
import PageDetailsModal from "@/components/modules/bet-details/PageDetailsModal.vue";
import RenamePageModal from "@/components/modules/bet-details/RenamePageModal.vue";
import DeletePageModal from "@/components/modules/bet-details/DeletePageModal.vue";
import DownlineSummary from "@/components/modules/bet-details/DownlineSummary.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import { DdPage, StoredPageType } from "@/CodegenTypes";
import {
  buildDdPageDetailsRows,
  PageDetailsRow,
  PageDetailsUser,
} from "./pageDetailsRows";

import { sumBy, startCase, first } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { formatCount } from "@/helpers/formatCount";
import { errorMutationNotification } from "@/utils";
import { customMoment, vueApp } from "@/main";
import { FEATURE_TOGGLES } from "@/config";
import { useTranslation } from "@/store/translation";
import { computed, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'PageDetails',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const selectedPage = ref<null | PageDetailsRow>(null);
const showPageDetailModal = ref(false);
const showRenamePageModal = ref(false);
const showDeletePageModal = ref(false);
const showAllPageDetails = ref(false);
const showAllPageDetailsScopeKey = ref<string | null>(null);

const selectedStartReleaseDate = computed(() =>
  getRouteQueryValue(route.query.startReleaseDate)
);
const selectedUserId = computed(() => getRouteQueryValue(route.query.userId));
const pageDetailsScopeKey = computed(
  () => `${selectedUserId.value || ""}:${selectedStartReleaseDate.value || ""}`
);
const includeAllPageDetails = computed(
  () =>
    showAllPageDetails.value &&
    showAllPageDetailsScopeKey.value === pageDetailsScopeKey.value
);
const userPagesSummaryVariables = computed(() => {
  const releaseDate = customMoment(selectedStartReleaseDate.value)
    .startOf("day")
    .toDate();
  return {
    releaseDateStart: releaseDate,
    releaseDateEnd: releaseDate,
    behalfOf: selectedUserId.value,
    includeDownlines: includeAllPageDetails.value,
  };
});

const {
  result: userPagesSummaryResult,
  loading: apolloLoading,
  refetch,
} = useQuery<{ user: PageDetailsUser }>(
  queries.userPagesSummary,
  userPagesSummaryVariables,
  {
    enabled: computed(() => !!selectedStartReleaseDate.value),
    fetchPolicy: "cache-and-network",
  }
);

const user = computed(() => userPagesSummaryResult.value?.user ?? null);
const pageDetailsData = computed(() =>
  buildDdPageDetailsRows(user.value, {
    includeDownlines: includeAllPageDetails.value,
  })
);
const pageDetailTotal = computed(() => {
  const data = pageDetailsData.value;
  return {
    big: sumBy(data, (d) => d.big).toFixed(2),
    small: sumBy(data, (d) => d.small).toFixed(2),
    amount: formatDollar(sumBy(data, (d) => d.amount)),
    rebate: formatDollar(sumBy(data, (d) => d.rebate)),
    payable: formatDollar(sumBy(data, (d) => d.payable)),
  };
});

watch([selectedUserId, selectedStartReleaseDate], resetPageDetailsScope);

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? first(query) : query;
}

function rowTestId(value: string) {
  return String(value || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function loadAllDownlinePageDetails() {
  showAllPageDetails.value = true;
  showAllPageDetailsScopeKey.value = pageDetailsScopeKey.value;
  closePageModals();
}

function resetPageDetailsScope() {
  showAllPageDetails.value = false;
  showAllPageDetailsScopeKey.value = null;
  closePageModals();
}

function closePageModals() {
  selectedPage.value = null;
  showPageDetailModal.value = false;
  showRenamePageModal.value = false;
  showDeletePageModal.value = false;
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD/MM/YYYY hh:mm:ss A");
}

async function savePage(id: DdPage["id"]) {
  const result = await apolloClient.query<{ page: DdPage }>({
    query: queries.page,
    variables: {
      id,
    },
  });
  const page = result.data.page;

  if (!page) {
    return;
  }

  vueApp.$confirm.require({
    header: "Save bet?",
    message: "Bet page will be saved under Saved Bets",
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        const result = await apolloClient.mutate({
          mutation: mutations.saveDdEntry,
          variables: {
            page: {
              id: undefined,
              page: page.name,
              userId: page.userId,
              entries: page.entries.map((e) => ({
                number: e.number,
                day: 7,
                big: e.big,
                small: e.small,
                mode: e.mode,
              })),
              storedPageType: StoredPageType.Saved,
            },
          },
        });
        const pageId = result.data.saveDdEntry;

        vueApp.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Page saved to Saved Bets",
          life: 3000,
        });

        router.push({
          name: "saved bet",
          query: { pageId, userId: page.userId },
        });
      } catch (err) {
        errorMutationNotification(err);
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });

  return id;
}

function deletePage(id: DdPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showDeletePageModal.value = true;
}

function renamePage(id: DdPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showRenamePageModal.value = true;
}

function reloadPageName() {
  refetch(userPagesSummaryVariables.value);
}

function reloadAll() {
  refetch(userPagesSummaryVariables.value);
}

function viewPageDetails(id: DdPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showPageDetailModal.value = true;
}

return { __sfc: true,route, router, $t, t, selectedPage, showPageDetailModal, showRenamePageModal, showDeletePageModal, showAllPageDetails, showAllPageDetailsScopeKey, selectedStartReleaseDate, selectedUserId, pageDetailsScopeKey, includeAllPageDetails, userPagesSummaryVariables, userPagesSummaryResult, apolloLoading, refetch, user, pageDetailsData, pageDetailTotal, getRouteQueryValue, rowTestId, loadAllDownlinePageDetails, resetPageDetailsScope, closePageModals, formatDate, savePage, deletePage, renamePage, reloadPageName, reloadAll, viewPageDetails, PageDetailsModal, RenamePageModal, DeletePageModal, DownlineSummary, startCase, formatDollar, formatCount, FEATURE_TOGGLES }
}

})